import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ServicoCriptografiaLocal {
  private readonly prefixo = 'v1';
  private readonly iteracoes = 120000;

  private readonly chaves = new Map<string, Promise<CryptoKey>>();

  async criptografarJson(valor: unknown, tokenTerminal: string, contexto: string): Promise<string> {
    const chave = await this.derivarChave(tokenTerminal, contexto);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const texto = new TextEncoder().encode(JSON.stringify(valor));
    const cifrado = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, chave, texto);
    return [
      this.prefixo,
      this.base64(iv),
      this.base64(new Uint8Array(cifrado))
    ].join(':');
  }

  async descriptografarJson<T>(valorCriptografado: string, tokenTerminal: string, contexto: string): Promise<T> {
    const partes = valorCriptografado.split(':');
    if (partes.length !== 3 || partes[0] !== this.prefixo) {
      throw new Error('Formato criptografico local invalido.');
    }

    const chave = await this.derivarChave(tokenTerminal, contexto);
    const iv = this.bytesBase64(partes[1]);
    const dados = this.bytesBase64(partes[2]);
    const aberto = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, chave, dados);
    return JSON.parse(new TextDecoder().decode(aberto)) as T;
  }

  private async derivarChave(tokenTerminal: string, contexto: string) {
    if (!crypto?.subtle) {
      throw new Error('Criptografia local indisponivel neste navegador.');
    }
    if (!tokenTerminal) {
      throw new Error('Token de terminal obrigatorio para criptografia local.');
    }

    const chaveCache = `${contexto}:${tokenTerminal}`;
    const chaveExistente = this.chaves.get(chaveCache);
    if (chaveExistente) {
      return chaveExistente;
    }

    const derivacao = this.gerarChave(tokenTerminal, contexto).catch(erro => {
      this.chaves.delete(chaveCache);
      throw erro;
    });
    this.chaves.set(chaveCache, derivacao);
    return derivacao;
  }

  private async gerarChave(tokenTerminal: string, contexto: string) {
    const material = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(tokenTerminal),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: new TextEncoder().encode(`pontojwm:${contexto}`),
        iterations: this.iteracoes,
        hash: 'SHA-256'
      },
      material,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private base64(bytes: Uint8Array) {
    let texto = '';
    bytes.forEach(byte => texto += String.fromCharCode(byte));
    return btoa(texto);
  }

  private bytesBase64(valor: string) {
    const texto = atob(valor);
    return Uint8Array.from(texto, caractere => caractere.charCodeAt(0));
  }
}
